import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, User, Clock } from "lucide-react";
import { blogPosts } from "./BlogPage";
import { articleSchema, breadcrumbSchema, usePageSeo } from "./seo";
const LOGO_URL = "/logo_suanuvem.avif";

const postContent = {
  1: [
    {
      heading: "Comece pelo inventário da infraestrutura atual",
      paragraphs: [
        "Uma migração para cloud segura começa antes da escolha da plataforma. O primeiro passo é mapear servidores, bancos de dados, aplicações, integrações, dependências, janelas de uso, requisitos de segurança e contratos de SLA.",
        "Esse inventário evita mover problemas antigos para um ambiente novo. Também ajuda a definir quais cargas podem ser migradas rapidamente, quais exigem modernização e quais devem permanecer em ambiente híbrido ou cloud privada.",
      ],
      bullets: [
        "Classifique aplicações por criticidade, dependência e complexidade.",
        "Mapeie requisitos de LGPD, auditoria, localidade de dados e controle de acesso.",
        "Calcule consumo atual de CPU, memória, armazenamento, rede e licenças.",
      ],
    },
    {
      heading: "Escolha a estratégia certa de migração",
      paragraphs: [
        "Nem toda carga deve seguir o mesmo caminho. Lift-and-shift pode ser adequado para reduzir prazo, mas sistemas legados, bancos sensíveis e aplicações críticas normalmente precisam de ajustes de arquitetura, automação e testes antes do cutover.",
      ],
      bullets: [
        "Rehost para cargas simples e com baixa dependência.",
        "Replatform para melhorar banco, storage, rede ou escala sem reescrever tudo.",
        "Refactor quando a aplicação precisa ganhar elasticidade, resiliência ou arquitetura cloud native.",
      ],
    },
    {
      heading: "Reduza downtime com testes e plano de cutover",
      paragraphs: [
        "O cutover deve ter roteiro, responsáveis, critérios de rollback e validações objetivas. Em ambientes críticos, a migração precisa incluir réplica de dados, testes de carga, validação de DNS, observabilidade e acompanhamento próximo nas primeiras horas de produção.",
      ],
      bullets: [
        "Execute migrações piloto antes das cargas mais críticas.",
        "Defina janelas de mudança e plano de reversão.",
        "Valide performance, segurança, backup e monitoramento antes de encerrar o ambiente antigo.",
      ],
    },
  ],
  2: [
    {
      heading: "DevOps resolve previsibilidade, não apenas velocidade",
      paragraphs: [
        "DevOps as a Service combina automação, padronização e operação assistida para reduzir gargalos entre desenvolvimento, infraestrutura e segurança. O objetivo não é apenas fazer deploy mais rápido, mas entregar com menos risco e mais capacidade de repetir processos.",
        "Pipelines de CI/CD, infraestrutura como código e GitOps tornam ambientes auditáveis. Isso reduz configurações manuais, melhora rastreabilidade e diminui diferenças entre desenvolvimento, homologação e produção.",
      ],
      bullets: [
        "CI/CD para build, testes, análise de qualidade e deploy automatizado.",
        "Terraform, Ansible ou ferramentas equivalentes para infraestrutura reproduzível.",
        "GitOps para manter estado desejado, histórico e revisão de mudanças.",
      ],
    },
    {
      heading: "Containers e Kubernetes exigem governança",
      paragraphs: [
        "Containers aceleram entregas, mas também aumentam a necessidade de padrões. Imagens, registries, secrets, políticas de rede, escalabilidade e observabilidade precisam ser tratados desde o início para evitar plataformas difíceis de operar.",
      ],
      bullets: [
        "Defina imagens base, versionamento e verificação de vulnerabilidades.",
        "Implemente limites de recursos, probes e estratégias de rollout.",
        "Crie dashboards e alertas por serviço, ambiente e jornada crítica.",
      ],
    },
    {
      heading: "O resultado esperado é uma operação mais madura",
      paragraphs: [
        "Quando bem implementado, DevOps reduz lead time, melhora rollback, diminui falhas por configuração manual e aumenta a autonomia dos times. Para empresas sem equipe especializada disponível, o modelo como serviço acelera essa maturidade sem sobrecarregar o time interno.",
      ],
    },
  ],
  3: [
    {
      heading: "FinOps começa com visibilidade por produto, time e ambiente",
      paragraphs: [
        "Redução de custos em nuvem depende de dados confiáveis. Sem tagging, separação por centro de custo e dashboards de consumo, a fatura vira um número agregado que não explica desperdício, tendência ou responsabilidade.",
        "A primeira etapa de FinOps é organizar a alocação: quem consome, para qual produto, em qual ambiente e com qual impacto no negócio.",
      ],
      bullets: [
        "Padronize tags obrigatórias para projeto, ambiente, dono e centro de custo.",
        "Crie budgets e alertas para evitar surpresas na fatura.",
        "Separe custos recorrentes, variáveis, compartilhados e ociosos.",
      ],
    },
    {
      heading: "Rightsizing e compromissos precisam de critério técnico",
      paragraphs: [
        "Desligar recursos pode gerar economia imediata, mas o ganho sustentável vem de rightsizing, reservas, savings plans, políticas de desligamento e revisões frequentes. Esses ajustes precisam considerar sazonalidade, SLA, performance e risco operacional.",
      ],
      bullets: [
        "Reduza instâncias superdimensionadas com base em métricas reais.",
        "Aplique compromissos apenas onde há previsibilidade de uso.",
        "Automatize desligamento de ambientes não produtivos quando fizer sentido.",
      ],
    },
    {
      heading: "FinOps é uma rotina, não um projeto pontual",
      paragraphs: [
        "A governança financeira em cloud funciona melhor quando engenharia, operações e financeiro revisam consumo periodicamente. Assim, novas arquiteturas já nascem com custo previsto e a empresa deixa de tratar otimização como ação emergencial.",
      ],
    },
  ],
  4: [
    {
      heading: "DevSecOps antecipa controles de segurança",
      paragraphs: [
        "Segurança na nuvem não pode aparecer apenas no fim do projeto. DevSecOps integra controles ao pipeline, a infraestrutura e a operação para identificar riscos cedo e corrigir vulnerabilidades antes que cheguem à produção.",
        "Essa abordagem reduz retrabalho, melhora conformidade e cria evidências para auditorias sem depender de processos manuais isolados.",
      ],
      bullets: [
        "SAST, DAST e SCA integrados ao CI/CD.",
        "Políticas de secrets, imagens e dependências vulneráveis.",
        "Hardening de servidores, containers, Kubernetes e redes.",
      ],
    },
    {
      heading: "IAM e segmentação reduzem superfície de ataque",
      paragraphs: [
        "Boa parte dos incidentes envolve credenciais, permissões excessivas ou redes permissivas demais. Por isso, identidade, acesso mínimo, MFA, logs de auditoria e segmentação devem fazer parte da arquitetura base.",
      ],
      bullets: [
        "Aplique o princípio do menor privilégio.",
        "Revise permissões por função e remova acessos antigos.",
        "Use redes privadas, firewalls, security groups e políticas explícitas.",
      ],
    },
    {
      heading: "Segurança precisa continuar depois do deploy",
      paragraphs: [
        "Ambientes cloud mudam todos os dias. Uma estratégia madura inclui monitoramento de postura, gestão de vulnerabilidades, resposta a incidentes, backup e revisões periódicas de conformidade com LGPD, ISO 27001, SOC 2 ou PCI DSS quando aplicável.",
      ],
    },
  ],
  5: [
    {
      heading: "Monitoramento mostra sintomas; observabilidade explica causas",
      paragraphs: [
        "Monitoramento tradicional responde se algo está no ar. Observabilidade ajuda a entender por que uma falha aconteceu, qual usuário foi impactado, qual serviço degradou e qual mudança pode ter causado o incidente.",
        "Para sistemas distribuídos, a combinação de métricas, logs e traces é essencial para reduzir MTTR e evitar que times investiguem no escuro.",
      ],
      bullets: [
        "Métricas indicam tendência, saturação e comportamento de recursos.",
        "Logs registram eventos e erros relevantes para investigação.",
        "Traces mostram o caminho de uma requisição entre serviços.",
      ],
    },
    {
      heading: "Alertas devem representar impacto real",
      paragraphs: [
        "Um ambiente cheio de alertas ruidosos tende a ser ignorado. Alertas eficientes estão ligados a SLOs, jornadas críticas e sinais que exigem ação. Isso reduz fadiga operacional e melhora a resposta a incidentes.",
      ],
      bullets: [
        "Crie dashboards por público: negócio, aplicação e infraestrutura.",
        "Priorize alertas de indisponibilidade, latência, erro e saturação.",
        "Documente runbooks para incidentes recorrentes.",
      ],
    },
    {
      heading: "Observabilidade também melhora decisão de arquitetura",
      paragraphs: [
        "Com dados consistentes, a empresa consegue identificar gargalos, planejar capacidade, medir experiência do usuário e justificar investimentos em escalabilidade, refatoração ou automação.",
      ],
    },
  ],
  6: [
    {
      heading: "RTO e RPO definem a estratégia de recuperação",
      paragraphs: [
        "Disaster recovery não começa pela ferramenta de backup. Primeiro, a empresa precisa definir quanto tempo um serviço pode ficar indisponível (RTO) e quanto dado pode ser perdido em um incidente (RPO).",
        "Esses objetivos orientam arquitetura, replicação, frequência de backup, custo e complexidade operacional.",
      ],
      bullets: [
        "Sistemas críticos podem exigir replicação e failover rápido.",
        "Sistemas administrativos podem aceitar janelas maiores de recuperação.",
        "Dados regulados precisam de retenção, criptografia e evidências de restauração.",
      ],
    },
    {
      heading: "Backup sem teste de restauração é apenas uma promessa",
      paragraphs: [
        "Muitas empresas descobrem falhas de backup somente durante um incidente. Por isso, o plano deve incluir testes periódicos, validação de integridade, documentação de runbooks e simulações de recuperação.",
      ],
      bullets: [
        "Teste restauração de bancos, arquivos, VMs e containers.",
        "Documente responsáveis, contatos, prioridades e ordem de retomada.",
        "Revise retenção, criptografia e localidade dos dados.",
      ],
    },
    {
      heading: "Continuidade de negócio exige operação preparada",
      paragraphs: [
        "Um bom plano de DR envolve tecnologia, processo e pessoas. Além de backups e replicação, a empresa precisa de monitoramento, comunicação de crise, controle de mudanças e revisões periódicas sempre que a arquitetura mudar.",
      ],
    },
  ],
};

function BlogPostPage() {
  const { postId } = useParams();
  const post = blogPosts.find(p => p.id === parseInt(postId));
  const postPath = `/blog/${postId}`;

  usePageSeo({
    title: post ? `${post.title} | Sua Nuvem` : "Artigo não encontrado | Sua Nuvem",
    description: post
      ? post.excerpt
      : "Leia conteúdos técnicos da Sua Nuvem sobre DevOps, cloud computing, FinOps e segurança.",
    path: postPath,
    type: "article",
    structuredData: post
      ? [
          articleSchema(post, postPath),
          breadcrumbSchema([
            { name: "Início", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: post.title, path: postPath },
          ]),
        ]
      : [],
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [postId]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="min-h-screen bg-brand-dark text-white pt-32 md:pt-40 pb-24 px-6 md:px-12">
      <div className="max-w-4xl mx-auto">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-brand-sage hover:underline mb-8"
          data-testid="back-to-blog"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar para Blog
        </Link>

        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Category Badge */}
          <div className="mb-6">
            <span className="inline-block px-4 py-2 bg-brand-sage/10 border border-brand-sage/30 rounded-full text-sm font-semibold text-brand-sage uppercase tracking-wider">
              {post.category}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
            {post.title}
          </h1>

          {/* Meta Info */}
          <div className="flex flex-wrap gap-6 text-gray-400 mb-12 pb-8 border-b border-white/10">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5" />
              <span>{post.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span>{post.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span>{post.readTime} de leitura</span>
            </div>
          </div>

          {/* Content */}
          <div className="prose prose-invert prose-lg max-w-none">
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              {post.excerpt}
            </p>

            <div className="space-y-6 text-gray-300">
              {postContent[post.id].map((section) => (
                <section key={section.heading}>
                  <h2 className="text-3xl font-bold text-white mt-12 mb-4">{section.heading}</h2>
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                  {section.bullets && (
                    <ul className="list-disc pl-6 space-y-2 my-6">
                      {section.bullets.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}

              <h2 className="text-3xl font-bold text-white mt-12 mb-4">Como a Sua Nuvem pode ajudar</h2>
              <p>
                A Sua Nuvem atua no diagnóstico, desenho de arquitetura, implementação e sustentação de ambientes
                cloud, DevOps, FinOps, segurança e continuidade de negócios. O trabalho combina execução técnica,
                documentação operacional e acompanhamento contínuo para reduzir risco e aumentar previsibilidade.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 p-8 bg-brand-surface/30 border border-brand-sage/20 rounded-xl text-center">
            <h3 className="text-2xl font-bold mb-4">Quer saber mais sobre este tema?</h3>
            <p className="text-gray-300 mb-6">
              Entre em contato com nossos especialistas para uma consulta personalizada.
            </p>
            <a
              href="/#contato"
              className="inline-block bg-brand-sage text-brand-dark px-8 py-4 rounded-md font-semibold hover:bg-opacity-90 transition-all text-center"
            >
              Falar com Especialista
            </a>
          </div>
        </motion.article>
      </div>
    </div>
  );
}

export default BlogPostPage;
